import { useCallback, useMemo } from 'react';

import { Checkbox, CheckboxChangeEvent } from 'primereact/checkbox';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { useSearchParams } from 'react-router-dom';

import { CalendarWrapper as Calendar } from '@/components/common/calendar-wrapper';
import { useSites } from '@/services/sites';
import { getUrlFormattedDate } from '@/utils';

import { useFertilizationContext } from './useFertilizationContext';

type Props = {
  className?: string;
};

export const FertilizationConfig = ({ className }: Props) => {
  const { poolSires, setPoolSires, updateFilters } = useFertilizationContext();
  const [params, setParams] = useSearchParams();

  const fertilizationSites = useSites({ filter: 'fertilization' });
  const fertilizationSite = params.get('site');
  const fertilizationDate = params.get('date');

  const selectedFertilizationSite = useMemo(() => {
    return fertilizationSites?.data?.find(
      (site) => site.id === fertilizationSite,
    )?.id;
  }, [fertilizationSite, fertilizationSites]);
  const handleSiteChange = useCallback(
    (e: DropdownChangeEvent) => {
      const newParams = new URLSearchParams(params);
      if (e.value) {
        newParams.set('site', e.value);
      } else {
        newParams.delete('site');
      }
      setParams(newParams);
    },
    [params, setParams],
  );

  const handleDateChange = useCallback(
    (e: DropdownChangeEvent) => {
      const newParams = new URLSearchParams(params);
      if (e.value instanceof Date) {
        newParams.set('date', getUrlFormattedDate(e.value));
      } else {
        newParams.delete('date');
      }
      setParams(newParams);
    },
    [params, setParams],
  );
  const handlePoolSires = useCallback(
    (e: CheckboxChangeEvent) => {
      setPoolSires(!!e.checked);
      updateFilters(!!e.checked);
    },
    [setPoolSires, updateFilters],
  );

  return (
    <div className={`flex flex-wrap items-center gap-4 ${className ?? ''}`}>
      <Calendar
        value={fertilizationDate ? new Date(fertilizationDate) : null}
        onChange={handleDateChange}
        placeholder="Donor Schedule Date"
        showIcon
        className="w-60"
      />

      <Dropdown
        options={fertilizationSites?.data ?? undefined}
        optionValue="id"
        optionLabel="name"
        value={selectedFertilizationSite}
        onChange={handleSiteChange}
        placeholder="Fert Site"
        className="w-60"
      />

      <span className="inline-flex items-center gap-2">
        <span>Pool Sires:</span>
        <Checkbox
          checked={poolSires}
          onChange={(e) => {
            handlePoolSires(e);
          }}
        />
      </span>
    </div>
  );
};
